import { createAction, createAsyncThunk } from '@reduxjs/toolkit'
import { APIError, authenticatedRequest, getExceptionPayload } from '../axiosConfiguration'
import {
  FixationAlloyManagementItem,
  FixationConfirmRequest, FixationsAllRequest, FixationsAllRequestsData,
  FixationsManagementData,
  FixationsManagementRequest,
  UserCompaniesManagementItem,
  UserCustomersManagementItem,
  UserSaleLineManagementItem,
  UserSaleLineParams,
} from '../type/fixations'
import * as layoutActions from './layout'
import i18next from 'i18next'

export const getFixations = createAsyncThunk<FixationsManagementData, FixationsManagementRequest, { rejectValue: APIError }>(
  'fixations/getFixations',
  async (data: any, { dispatch, rejectWithValue }) => {

    dispatch(layoutActions.startLoading())

    try {
      const instance = await authenticatedRequest()
      const queryParams = new URLSearchParams()
      const currentLanguage = localStorage.getItem("selectedLanguage") ?? 'en'
      queryParams.append('language_code', currentLanguage)
      const res = await instance.post<FixationsManagementData>(`/fixation?${queryParams.toString()}`, data)

      dispatch(layoutActions.endLoading())

      return res.data
    } catch (error) {
      const err = getExceptionPayload(error, 'fixations:error.getFixations')

      dispatch(layoutActions.endLoading())
      dispatch(layoutActions.showToast({ summary: err.message, severity: 'error' }))

      return rejectWithValue(err)
    }
  },
)

export const getUserCompanies = createAsyncThunk<UserCompaniesManagementItem[], void, { rejectValue: APIError }>
  ('users/getUserCompanies',
    async (_, { dispatch, rejectWithValue }) => {
  dispatch(layoutActions.startLoading())

  try {
    const instance = await authenticatedRequest()
    const res = await instance.get<UserCompaniesManagementItem[]>(`/user/company`)

    dispatch(layoutActions.endLoading())

    return res.data
  } catch (error) {
    const err = getExceptionPayload(error, 'fixations:error.getUserCompanies')

    dispatch(layoutActions.endLoading())
    dispatch(layoutActions.showToast({ summary: err.message, severity: 'error' }))

    return rejectWithValue(err)
  }
})

export const getUserCustomers = createAsyncThunk<UserCustomersManagementItem[], string | undefined, { rejectValue: APIError }>
('users/getUserCustomers',
  async (company, { dispatch, rejectWithValue }) => {
    dispatch(layoutActions.startLoading())

    try {
      const instance = await authenticatedRequest()
      const queryParams = new URLSearchParams()
      if (company) {
        queryParams.append('company', company)
      }
      const res = await instance.get<UserCustomersManagementItem[]>(`/user/customer?${queryParams.toString()}`)

      dispatch(layoutActions.endLoading())

      return res.data
    } catch (error) {
      const err = getExceptionPayload(error, 'fixations:error.getUserCustomers')

      dispatch(layoutActions.endLoading())
      dispatch(layoutActions.showToast({ summary: err.message, severity: 'error' }))

      return rejectWithValue(err)
    }
})

export const getUserSaleLine = createAsyncThunk<UserSaleLineManagementItem[], UserSaleLineParams | undefined, { rejectValue: APIError }>
('users/getUserSaleLine',
  async (params, { dispatch, rejectWithValue }) => {
    dispatch(layoutActions.startLoading())

    try {
      const instance = await authenticatedRequest()
      const queryParams = new URLSearchParams()
      if (params?.company) {
        queryParams.append('company', params.company)
      }
      if (params?.customer) {
        queryParams.append('customer', params.customer)
      }

      const res = await instance.get<UserSaleLineManagementItem[]>(`/user/saleline${queryParams.toString() ? `?${queryParams.toString()}` : ''}`)

      dispatch(layoutActions.endLoading())

      return res.data
    } catch (error) {
      const err = getExceptionPayload(error, 'fixations:error.getUserSaleLine')

      dispatch(layoutActions.endLoading())
      dispatch(layoutActions.showToast({ summary: err.message, severity: 'error' }))

      return rejectWithValue(err)
    }
})

export const getFixationAlloy = createAsyncThunk<FixationAlloyManagementItem[], UserSaleLineParams | undefined, { rejectValue: APIError }>
('fixation/getFixationAlloy',
  async (params, { dispatch, rejectWithValue }) => {
    dispatch(layoutActions.startLoading())

    try {
      const instance = await authenticatedRequest()
      const queryParams = new URLSearchParams()
      if (params?.company) {
        queryParams.append('company', params.company)
      }
      if (params?.customer) {
        queryParams.append('customer', params.customer)
      }

      const res = await instance.get<FixationAlloyManagementItem[]>(`/fixation/alloy${queryParams.toString() ? `?${queryParams.toString()}` : ''}`)

      dispatch(layoutActions.endLoading())

      return res.data
    } catch (error) {
      const err = getExceptionPayload(error, 'userManagement:error.getFixationAlloy')

      dispatch(layoutActions.endLoading())
      dispatch(layoutActions.showToast({ summary: err.message, severity: 'error' }))

      return rejectWithValue(err)
    }
  })

export const sendNewFixation = createAsyncThunk<void, FixationConfirmRequest, { rejectValue: APIError }>(
  'fixations/sendNewFixation',
  async (payload, { dispatch, rejectWithValue }) => {
    try {
      const instance = await authenticatedRequest()

      await instance.post(`/fixation/request`, payload)
      dispatch(
        layoutActions.showToast({
          summary: i18next.t('fixations:send.notification.success'),
          severity: 'success',
        }),
      )
    } catch (error) {
      const err = getExceptionPayload(error, 'fixations:send.notification.error')

      dispatch(layoutActions.showToast({ summary: err.message, severity: 'error' }))
      return rejectWithValue(err)
    }
  },
)

export const clearNewFixation = createAction<void>('fixation/clearNewFixation')

// All Requests
export const getFixationsRequests = createAsyncThunk<FixationsAllRequestsData, FixationsAllRequest, { rejectValue: APIError }>(
  'fixations/request/get',
  async (data: any, { dispatch, rejectWithValue }) => {

    dispatch(layoutActions.startLoading())

    try {
      const instance = await authenticatedRequest()
      const res = await instance.post<FixationsAllRequestsData>('/fixation/request/get', data)

      dispatch(layoutActions.endLoading())

      return res.data
    } catch (error) {
      const err = getExceptionPayload(error, 'fixations:error.getFixations')

      dispatch(layoutActions.endLoading())
      dispatch(layoutActions.showToast({ summary: err.message, severity: 'error' }))

      return rejectWithValue(err)
    }
  },
)
